/**
 * Unified Loading Screen Component
 *
 * Provides a consistent loading experience across the application.
 * Used during auth initialization and crisis loading states.
 *
 * Usage:
 *   // With card (for auth/protected routes)
 *   <LoadingScreen showCard={true} />
 *
 *   // Simple spinner (for crisis loading)
 *   <LoadingScreen showCard={false} />
 */

import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '../ui/card'
import { Layout } from '../layout'

interface LoadingScreenProps {
  showCard?: boolean
  message?: string
}

export function LoadingScreen({ showCard = true, message }: LoadingScreenProps) {
  const { t } = useTranslation()
  const loadingMessage = message || t('algemeen.laden', 'Loading...')

  if (showCard) {
    // Show loading screen with card (for auth/protected routes)
    return (
      <Layout showNavigation={false}>
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <Card variant="elevated" className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground">{loadingMessage}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  // Simple loading spinner (for crisis guard)
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground">{loadingMessage}</p>
      </div>
    </div>
  )
}
